import type { ReactElement } from 'react';
import { useRef, useState, useEffect } from 'react';
import { useUIDSeed } from 'react-uid';
import { useMediaQuery } from 'react-responsive';
import { BreakpointMedia } from 'config';
import Collapse from 'react-bootstrap/Collapse';

import MenuItem from 'models/MenuItem';

import ScrollButton from './ScrollButton';
import SkuItem from './SkuItem';

type Props = {
  items: MenuItem[];
  cartId?: number;
  expanded?: boolean;
  showFavoritesWidget?: boolean;
  showFavoritesAnnouncement?: boolean;
  openProductsInNewTab?: boolean;
  children?: ReactElement;
};

function SkuGallery({
  items,
  cartId,
  expanded = true,
  showFavoritesWidget = true,
  showFavoritesAnnouncement = false,
  openProductsInNewTab = false,
  children,
}: Props): ReactElement {
  const ITEM_MARGIN = 16;
  const TEASER_ITEM_WIDTH = 32;

  const containerElem = useRef(null);
  const seed = useUIDSeed();

  const sm = useMediaQuery({ query: BreakpointMedia.SM });
  const lg = useMediaQuery({ query: BreakpointMedia.LG });

  let itemsPerPage = 1;
  if (sm) itemsPerPage = 2;
  if (lg) itemsPerPage = 3;

  const [isPrevDisabled, setPrevDisabled] = useState(true);
  const [isNextDisabled, setNextDisabled] = useState(
    items.length <= itemsPerPage,
  );

  useEffect(() => (
    setNextDisabled(items.length <= itemsPerPage)
  ), [itemsPerPage, items.length]);

  const scrollTo = (direction: 'next' | 'prev') => {
    // @ts-expect-error TODO: fix null safety
    const itemElem = containerElem.current.children[0].children[0];
    const itemWidth = itemElem.clientWidth + ITEM_MARGIN;
    // @ts-expect-error TODO: fix null safety
    let initialScrollPosition = containerElem.current.scrollLeft;
    // @ts-expect-error TODO: fix null safety
    const maxScrollPosition = containerElem.current.scrollWidth
    // @ts-expect-error TODO: fix null safety
      - containerElem.current.clientWidth;

    if (direction === 'prev') {
      initialScrollPosition = maxScrollPosition - initialScrollPosition;
    }

    initialScrollPosition += TEASER_ITEM_WIDTH;

    const scrolledItems = Math.round(
      (initialScrollPosition + (itemsPerPage * itemWidth)) / itemWidth,
    );

    let finalScrollPosition = (
      scrolledItems * itemWidth - Math.round(TEASER_ITEM_WIDTH / 2)
    );

    if (direction === 'prev') {
      finalScrollPosition = maxScrollPosition - finalScrollPosition;
    }

    if (scrolledItems + itemsPerPage >= items.length) {
      finalScrollPosition = direction === 'prev' ? 0 : maxScrollPosition;
    }
    // @ts-expect-error TODO: fix null safety
    containerElem.current.scrollLeft = finalScrollPosition;
  };

  const onScroll = () => {
    // @ts-expect-error TODO: fix null safety
    const maxScrollPosition = containerElem.current.scrollWidth
    // @ts-expect-error TODO: fix null safety
      - containerElem.current.clientWidth - 1;
    // @ts-expect-error TODO: fix null safety
    if (containerElem.current.scrollLeft === 0) {
      setPrevDisabled(true);
    } else {
      setPrevDisabled(false);
    }
    // @ts-expect-error TODO: fix null safety
    if (containerElem.current.scrollLeft >= maxScrollPosition) {
      setNextDisabled(true);
    } else {
      setNextDisabled(false);
    }
  };

  // moves thanksgiving item to front of list
  const ltoIndex = items.findIndex(
    (i) => i.sku.is_lto_box,
  );

  if (ltoIndex > -1) {
    const [ltoItem] = items.splice(ltoIndex, 1);
    items.unshift(ltoItem);
  }

  return (
    <Collapse in>
      <div className="c-recipe-gallery">
        <div
          className="c-recipe-gallery__scroll"
          ref={containerElem}
          onScroll={onScroll}
        >
          <div className="row">
            {items.map((menuItem, index) => (
              <SkuItem
                key={seed(index)}
                item={menuItem}
                cartId={cartId}
                hasFavoritesEnabled={showFavoritesWidget}
                itemIndex={index}
                showFavoritesAnnouncement={showFavoritesAnnouncement}
                openProductInNewTab={openProductsInNewTab}
              />
            ))}
          </div>

          <ScrollButton
            direction="prev"
            disabled={isPrevDisabled}
            onClick={() => scrollTo('prev')}
          />

          <ScrollButton
            direction="next"
            disabled={isNextDisabled}
            onClick={() => scrollTo('next')}
          />

          {children}
        </div>
      </div>
    </Collapse>
  );
}

export default SkuGallery;
